import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  VitaBeneficiarioService,
  VitaFaltanteField,
} from '../../../../services/vita-beneficiario.service';
import { sinTildes } from '../../../../../shared/utils/texto-sin-tildes.util';

export interface CompleteVitaBeneficiarioDialogData {
  beneficiarioId: string;
  variante: string;
  varianteLabel?: string;
  faltantes: VitaFaltanteField[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Completa los datos faltantes de un beneficiario Vita PARA SU VARIANTE, según
 * las reglas vivas de Vita. Los `faltantes` ya vienen filtrados/visibles desde
 * el backend (`/completitud`); acá solo se renderizan y se validan en básico.
 * Al guardar, el backend re-valida (`/completar`) y devuelve si quedó completo.
 */
@Component({
  selector: 'app-complete-vita-beneficiario-dialog',
  templateUrl: './complete-vita-beneficiario-dialog.component.html',
  styleUrls: ['./complete-vita-beneficiario-dialog.component.sass'],
})
export class CompleteVitaBeneficiarioDialogComponent {
  fields: VitaFaltanteField[] = [];
  values: Record<string, any> = {};
  errors: Record<string, string> = {};
  saving = false;
  serverError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<
      CompleteVitaBeneficiarioDialogComponent,
      boolean
    >,
    @Inject(MAT_DIALOG_DATA)
    public data: CompleteVitaBeneficiarioDialogData,
    private svc: VitaBeneficiarioService,
  ) {
    this.fields = data.faltantes || [];
    for (const f of this.fields) this.values[f.key] = '';
  }

  hintForField(f: VitaFaltanteField): string | null {
    if (f.type === 'email') return 'Ej: nombre@dominio.com';
    if (f.min && f.max) return `${f.min}–${f.max} caracteres`;
    if (f.min) return `Mínimo ${f.min} caracteres`;
    if (f.max) return `Máximo ${f.max} caracteres`;
    return null;
  }

  onInput(f: VitaFaltanteField, event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value ?? '';
    let next = raw;
    if (f.type === 'text') {
      next = sinTildes(raw)
        .toUpperCase()
        .replace(/^\s+/, '')
        .replace(/ {2,}/g, ' ');
    } else if (f.type === 'email') {
      next = raw.replace(/^\s+/, '');
    }
    if (next !== raw) target.value = next;
    this.values[f.key] = next;
    if (this.errors[f.key] && !this.validateField(f)) delete this.errors[f.key];
  }

  onBlur(f: VitaFaltanteField): void {
    const raw = this.values[f.key];
    const v = typeof raw === 'string' ? raw.trim() : raw;
    if (typeof raw === 'string' && raw !== v) this.values[f.key] = v;
    const err = this.validateField(f);
    if (err) this.errors[f.key] = err;
    else delete this.errors[f.key];
  }

  private validateField(f: VitaFaltanteField): string | null {
    const raw = this.values[f.key];
    const v = typeof raw === 'string' ? raw.trim() : raw;
    if (v === undefined || v === null || v === '') return 'Campo requerido';
    if ((f.type === 'text' || f.type === 'email') && typeof v === 'string') {
      if (f.min && v.length < f.min) return `Mínimo ${f.min} caracteres`;
      if (f.max && v.length > f.max) return `Máximo ${f.max} caracteres`;
    }
    if (f.type === 'email' && typeof v === 'string' && !EMAIL_RE.test(v)) {
      return 'Formato de email inválido';
    }
    if (f.type === 'select' && f.options) {
      const ok = f.options.some((o) => String(o.value) === String(v));
      if (!ok) return 'Seleccione una opción válida';
    }
    return null;
  }

  private validateAll(): boolean {
    this.errors = {};
    for (const f of this.fields) {
      const err = this.validateField(f);
      if (err) this.errors[f.key] = err;
    }
    return Object.keys(this.errors).length === 0;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    if (!this.validateAll()) return;
    const datos: Record<string, any> = {};
    // Mismo criterio que el alta: lo que se guarda va sin tildes (la ñ
    // incluida). El email y los select quedan intactos — uno se valida por
    // formato, el otro es un código de Vita.
    for (const f of this.fields) {
      const v = this.values[f.key];
      datos[f.key] =
        typeof v === 'string' && f.type !== 'email' && f.type !== 'select'
          ? sinTildes(v)
          : v;
    }
    this.saving = true;
    this.serverError = null;
    this.svc.completar(this.data.beneficiarioId, datos).subscribe({
      next: (res) => {
        this.saving = false;
        if (res.valid) {
          this.dialogRef.close(true);
        } else {
          // Quedaron faltantes (regla con `when` que se activó). Re-render.
          this.fields = res.faltantes || [];
          for (const f of this.fields) {
            if (this.values[f.key] === undefined) this.values[f.key] = '';
          }
          this.serverError =
            'Aún faltan datos. Completá los campos restantes.';
        }
      },
      error: (err) => {
        this.saving = false;
        this.serverError =
          typeof err === 'string' ? err : 'No se pudieron guardar los datos.';
      },
    });
  }
}
